/**
 * Отбор строк в базе игроков.
 *
 * Отдельно от страницы: условий стало столько, что проверять их глазами по
 * разметке нельзя, а тестами — можно.
 */

import type { Tier } from '@ak/shared';

import type { PlayerRow } from './player-sort.ts';

/** На сервере человек или уже вышел. */
export type PlayerStatus = 'all' | 'present' | 'left';

/**
 * Давно ли человек был на капте.
 *
 * `stale` — не был больше месяца, считая и тех, кто не был никогда: для
 * вопроса «кто перестал ходить» это одна и та же строка списка.
 */
export type PlayerActivity = 'all' | 'week' | 'month' | 'stale';

/** Признаки анкеты, которую надо разобрать руками. */
export type PlayerProblem = 'noStatic' | 'noCaptures' | 'unseen';

export interface PlayerFilters {
  query: string;
  /** Пустой список — тир не ограничивает выборку. */
  tiers: Tier[];
  status: PlayerStatus;
  activity: PlayerActivity;
  /** Несколько признаков соединяются «или»: это показ проблемных, а не пересечение. */
  problems: PlayerProblem[];
  /** `null` — порог не задан. Ноль порогом быть не может: он никого не отсекает. */
  minCaptures: number | null;
  minAvgDamage: number | null;
  minAvgKills: number | null;
}

export const EMPTY_FILTERS: PlayerFilters = {
  query: '',
  tiers: [],
  status: 'all',
  activity: 'all',
  problems: [],
  minCaptures: null,
  minAvgDamage: null,
  minAvgKills: null,
};

/** Задан ли хоть один отбор — по этому показывается кнопка «Сбросить». */
export function isFilterActive(filters: PlayerFilters): boolean {
  return (
    filters.query.trim() !== '' ||
    filters.tiers.length > 0 ||
    filters.status !== 'all' ||
    filters.activity !== 'all' ||
    filters.problems.length > 0 ||
    filters.minCaptures !== null ||
    filters.minAvgDamage !== null ||
    filters.minAvgKills !== null
  );
}

function matchesQuery(player: PlayerRow, needle: string): boolean {
  return (
    needle === '' ||
    player.gameNick.toLowerCase().includes(needle) ||
    player.staticId.toLowerCase().includes(needle) ||
    (player.discordName ?? '').toLowerCase().includes(needle) ||
    player.userId.includes(needle)
  );
}

function matchesActivity(player: PlayerRow, activity: PlayerActivity): boolean {
  if (activity === 'all') {
    return true;
  }

  if (player.combat === null) {
    // Кто не был ни разу, попадает только в «давно не был»: в остальных
    // окнах его нет, и придумывать ему нули незачем.
    return activity === 'stale';
  }

  if (activity === 'week') {
    return player.combat.capturesWeek > 0;
  }

  return activity === 'month' ? player.combat.capturesMonth > 0 : player.combat.capturesMonth === 0;
}

function matchesProblems(player: PlayerRow, problems: readonly PlayerProblem[]): boolean {
  if (problems.length === 0) {
    return true;
  }

  return problems.some((problem) => {
    if (problem === 'noStatic') {
      return player.staticId.trim() === '';
    }

    return problem === 'noCaptures' ? player.combat === null : player.discordName === null;
  });
}

/**
 * Пороги по боевым цифрам.
 *
 * У кого каптов не было, порог не проходит никогда: сравнивать не с чем, а
 * подставить ноль значило бы записать человека в слабые за то, что он просто
 * не выходил.
 */
function matchesThresholds(player: PlayerRow, filters: PlayerFilters): boolean {
  const { minCaptures, minAvgDamage, minAvgKills } = filters;
  if (minCaptures === null && minAvgDamage === null && minAvgKills === null) {
    return true;
  }

  if (player.combat === null) {
    return false;
  }

  return (
    (minCaptures === null || player.combat.captures >= minCaptures) &&
    (minAvgDamage === null || player.combat.avgDamage >= minAvgDamage) &&
    (minAvgKills === null || player.combat.avgKills >= minAvgKills)
  );
}

export function filterPlayers(players: readonly PlayerRow[], filters: PlayerFilters): PlayerRow[] {
  const needle = filters.query.trim().toLowerCase();

  return players.filter(
    (player) =>
      matchesQuery(player, needle) &&
      (filters.tiers.length === 0 || filters.tiers.includes(player.tier)) &&
      (filters.status === 'all' ||
        (filters.status === 'left' ? player.leftAt !== null : player.leftAt === null)) &&
      matchesActivity(player, filters.activity) &&
      matchesProblems(player, filters.problems) &&
      matchesThresholds(player, filters),
  );
}
